import { FC, useMemo, useState } from 'react';
import useSWR from 'swr';
import StockOrderCard from '../StockOrderCard/StockOrderCard';
import { getStockOrders } from '../../../../services/stockOrders';
import useStockOrdersModuleState from '../../../../state/useStockOrdersModuleState';
import { SORT_ORDER } from '../../../../types/global';
import CancelledOrdersOverlay from './components/CancelledOrdersOverlay/CancelledOrdersOverlay';
import StockOrderSkeletonLoader from './components/StockOrderSkeletonLoader/StockOrderSkeletonLoader';
import { SHIPMENT_STATUS_STOCK_ORDER } from '../../../../types/stockOrders';
import Arrows from 'assets/images/shopping/arrows';
import { EmptyStateTrello } from 'views/services/components';
import './StockOrdersList.scss';

/**
 * Componente que muestra el listado de órdenes de stock en columnas según su estado
 * Cada columna representa un estado diferente y se actualiza independientemente 
 */
const StockOrdersList: FC = () => {
  const [showCancelledOrders, setShowCancelledOrders] = useState(false);
  const [hasCancelledMatches, setHasCancelledMatches] = useState(false);
  const { stockOrdersFilters, stockOrdersSortFilter, searchText, propertyToFilterSearch } = useStockOrdersModuleState();

  const filtersQuery = useMemo(() => {
    if (!stockOrdersFilters || stockOrdersFilters.length === 0) return '';

    // Filtrar el array removiendo los items de sort
    const filteredFilters = stockOrdersFilters.filter(filter => filter.name !== 'sort');

    return filteredFilters.length > 0 ? `filters=${JSON.stringify(filteredFilters)}` : '';
  }, [stockOrdersFilters]);

  const sortQuery = useMemo(() => {
    const sortFilter = stockOrdersFilters?.find(filter => filter.name === 'sort');
    const propertyToSort = sortFilter?.values?.[0] || '';

    return `sort=${JSON.stringify({
      value: stockOrdersSortFilter?.value || SORT_ORDER.DESC,
      propertyToSort: propertyToSort ? propertyToSort : 'orderId'
    })}`;
  }, [stockOrdersSortFilter, stockOrdersFilters]);

  const searchQuery = useMemo(() => {
    if (!searchText || !propertyToFilterSearch) return '';
    return JSON.stringify({
      value: searchText,
      propertyToFilter: propertyToFilterSearch.propertyToFilter
    });
  }, [searchText, propertyToFilterSearch]);

  const { data: confirmedOrders, mutate: refreshConfirmed } = useSWR(
    ['stockOrders-confirmed', 1, filtersQuery, sortQuery, searchQuery],
    () => getStockOrders({ statusId: 1, filters: filtersQuery, search: searchQuery, sort: sortQuery })
  );

  const { data: inProcessOrders, mutate: refreshInProcess } = useSWR(
    ['stockOrders-inProcess', 2, filtersQuery, sortQuery, searchQuery],
    () => getStockOrders({ statusId: 2, filters: filtersQuery, search: searchQuery, sort: sortQuery })
  );

  const { data: delayedOrders, mutate: refreshDelayed } = useSWR(
    ['stockOrders-delayed', 4, filtersQuery, sortQuery, searchQuery],
    () => getStockOrders({ statusId: 4, filters: filtersQuery, search: searchQuery, sort: sortQuery })
  );

  const { data: deliveredOrders, mutate: refreshDelivered } = useSWR(
    ['stockOrders-delivered', 3, filtersQuery, sortQuery, searchQuery],
    () => getStockOrders({ statusId: 3, filters: filtersQuery, search: searchQuery, sort: sortQuery })
  );

  const refreshAll = () => {
    refreshConfirmed();
    refreshInProcess();
    refreshDelayed();
    refreshDelivered();
  };

  return (
    <>
      <div className="stockOrdersList">
        <div className="column">
          <div className="text-xs flex items-center justify-center gap-8 text-nudos-gray-500 mb-16">
            <div className="font-bold">Confirmado</div>
            <div>-</div>
            <div> Sin tracking y fecha</div>
            <div className="text-xs">({confirmedOrders?.length || 0})</div>
          </div>
          <div className="columnContent">
            {!confirmedOrders ? (
              <StockOrderSkeletonLoader />
            ) : (
              <>
                {confirmedOrders.map(order => (
                  <>
                    <StockOrderCard
                      key={order.orderId}
                      data={order}
                      isConfirmedColumn
                      shipmentStatusName={SHIPMENT_STATUS_STOCK_ORDER.CONFIRMED}
                    />
                  </>
                ))}
                {confirmedOrders?.length === 0 && (
                  <div className="w-full text-center">
                    <span className="block text-center text-nudos-gray-400">
                      Confirmados ({confirmedOrders?.length})
                    </span>
                    <div className="mx-auto w-fit">
                      <EmptyStateTrello text="No hay órdenes de compra confirmadas" icon="truck" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="spaceProcess">
          <div className="containerIcon">
            <Arrows />
          </div>
          <div className="spaceTitle">Manual</div>
        </div>
        <div className="column">
          <div className="text-xs flex items-center justify-center gap-8 text-nudos-gray-500 mb-16">
            <div className="font-bold">Proceso de entrega</div>
            <div>-</div>
            <div>Con tracking y fecha</div>
            <div className="text-xs">({inProcessOrders?.length || 0})</div>
          </div>
          <div className="columnContent">
            {!inProcessOrders ? (
              <StockOrderSkeletonLoader />
            ) : (
              <>
                {inProcessOrders.map(order => (
                  <StockOrderCard
                    key={order.orderId}
                    data={order}
                    refreshData={refreshAll}
                    shipmentStatusName={SHIPMENT_STATUS_STOCK_ORDER.CONFIRMED}
                  />
                ))}
                {inProcessOrders?.length === 0 && (
                  <div className="w-full text-center">
                    <span className="block text-center text-nudos-gray-400">
                      Proceso de entrega ({inProcessOrders?.length})
                    </span>
                    <div className="mx-auto w-fit">
                      <EmptyStateTrello text="No hay órdenes de compra activas" icon="truck" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="spaceProcess">
          <div className="containerIcon">
            <Arrows />
          </div>
          <div className="spaceTitle">Automatico</div>
        </div>
        <div className="column">
          <div className="text-xs flex items-center justify-center gap-8 text-nudos-gray-500 mb-16">
            <div className="font-bold">Atrasado</div>
            <div>-</div>
            <div>Superó la fecha estimada</div>
            <div className="text-xs">({delayedOrders?.length || 0})</div>
          </div>
          <div className="columnContent">
            {!delayedOrders ? (
              <StockOrderSkeletonLoader />
            ) : (
              <>
                {delayedOrders.map(order => (
                  <StockOrderCard
                    key={order.orderId}
                    data={order}
                    refreshData={refreshAll}
                    shipmentStatusName={SHIPMENT_STATUS_STOCK_ORDER.DELAYED}
                  />
                ))}
                {delayedOrders?.length === 0 && (
                  <div className="w-full text-center">
                    <span className="block text-center text-nudos-gray-400">Atrasado ({delayedOrders?.length})</span>
                    <div className="mx-auto w-fit">
                      <EmptyStateTrello text="No hay órdenes de compra atrasadas" icon="truck" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="spaceProcess">
          <div className="containerIcon">
            <Arrows />
          </div>
          <div className="spaceTitle automAndManual">Autom/ Manual</div>
        </div>
        <div className="column">
          <div className="text-xs flex items-center justify-center gap-8 text-nudos-gray-500 mb-16">
            <div className="font-bold">Entregado</div>
            <div>-</div>
            <div>Almacenado en bodega</div>
            <div className="text-xs">({deliveredOrders?.length || 0})</div>
          </div>
          <div className="columnContent">
            {!deliveredOrders ? (
              <StockOrderSkeletonLoader />
            ) : (
              <>
                {deliveredOrders.map(order => (
                  <StockOrderCard
                    key={order.orderId}
                    data={order}
                    refreshData={refreshAll}
                    shipmentStatusName={SHIPMENT_STATUS_STOCK_ORDER.DELIVERED}
                  />
                ))}
                {deliveredOrders?.length === 0 && (
                  <div className="w-full text-center">
                    <span className="block text-center text-nudos-gray-400">Entregado ({deliveredOrders?.length})</span>
                    <div className="mx-auto w-fit">
                      <EmptyStateTrello text="No hay órdenes de compra entregadas" icon="truck" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <button
          onClick={() => setShowCancelledOrders(true)}
          className={`showCancelledButton ${hasCancelledMatches ? 'sonarActive' : ''}`}
        >
          Ver órdenes canceladas
        </button>
      </div>

      <CancelledOrdersOverlay
        isOpen={showCancelledOrders}
        onClose={() => setShowCancelledOrders(false)}
        onMatchesFound={setHasCancelledMatches}
        searchText={searchText || ''}
        propertyToFilterSearch={propertyToFilterSearch}
      />
    </>
  );
};

export default StockOrdersList;
